<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<template>
	<button
		ref="mainElement"
		class="c-MultiSelect"
		:class="selectClassObject"
		:title="label"
		:disabled="disabled"
		@keydown.down.prevent
		@keydown.up.prevent
		@click.self="focusElem"
		@focus.capture="handleMenu(true)"
		@blur.capture="handleMenu(false)"
	>
		<transition name="fade">
			<span
				v-if="currValue.label"
				class="c-MultiSelect__value"
				@click.self="focusElem"
			>
				{{ currValue.label }}
			</span>
		</transition>
		<span
			class="c-MultiSelect__label"
			@click.self="focusElem"
		>
			{{ label }}
		</span>
		<i
			v-show="currValue.value && currValue.value.length"
			v-if="withClear"
			class="c-MultiSelect__clear"
			@click="clearSelection"
			@mousedown.prevent
		/>
		<i class="c-MultiSelect__decorator" />
		<ul
			v-if="menuVisible"
			class="c-MultiSelect__menu"
		>
			<li
				v-for="(opt, index) in selectItems"
				:key="index"
				class="c-MultiSelect__menuItem"
				:class="{ 'simpleOption': !opt.group }"
			>
				<input
					:id="`opt${index}`"
					v-model="groupData"
					type="checkbox"
					:value="opt.value"
					@change="selectOption(opt)"
				>
				<label
					:for="`opt${index}`"
					@keypress.enter="pickOption(index)"
					@mousedown.prevent
				>
					<span
						:key-index="index"
						:tabindex="index">{{ opt.label }}</span>
				</label>
			</li>
		</ul>
	</button>
</template>

<script>
export default {
	name: 'MultiSelect',
	props: {
		/**
     * The MultiSelect label
     */
		label: {
			type: String,
			required: true,
			default: () => ''
		},
		/**
     * The MultiSelect options for the dropdown
     */
		selectItems: {
			type: Array,
			required: true,
			default: () => [],
			validator: (data) => {
				const conditions = []
				data.forEach(item => {
					conditions.push(Object.keys(item).indexOf('label'))
					conditions.push(Object.keys(item).indexOf('value'))

					if (Object.keys(item).indexOf('group') > -1) {
						conditions.push(Object.keys(item).indexOf('group'))
					} else if (Object.keys(item).indexOf('groupValue') > -1) {
						conditions.push(Object.keys(item).indexOf('groupValue'))
					}
				})

				return data.length === 0 || conditions.every(cond => cond > -1)
			}
		},
		/**
     * The MultiSelect data variable
     */
		data: {
			type: [Number, String],
			required: false,
			default: () => undefined
		},
		/**
     * If the MultiSelect should display a clear input button
     * @values true, false
     */
		withClear: {
			type: Boolean,
			required: false,
			default: () => true
		},
		/**
     * If the MultiSelect is in an active state
     * @values true, false
     */
		isActive: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * If the MultiSelect is disabled (html attribute disabled)
     * @values true, false
     */
		disabled: {
			type: Boolean,
			required: false,
			default: () => false
		}
	},
	data () {
		return {
			menuVisible: false,
			isGrouping: false,
			groupData: [],
			currValue: {
				label: null,
				value: []
			}
		}
	},
	computed: {
		selectClassObject () {
			return {
				open: this.menuVisible,
				withValue: this.currValue.label && this.currValue.label.length > 0,
				isActive: this.isActive,
				isGrouping: this.isGrouping
			}
		}
	},
	watch: {
		data: {
			handler: function handleChange (newVal) {
				const groupCheck = this.selectItems.find(item => item.group)
				// eslint-disable-next-line no-unused-expressions
				groupCheck ? this.isGrouping = true : this.isGrouping = false

				if (newVal && newVal !== this.currValue.value.join(',')) {
					const value = newVal.split(',')
					value.forEach(val => {
						const option = this.selectItems.find(item => item.value === val)

						if (option) {
							if (this.groupData.indexOf(option.value) === -1) {
								this.groupData.push(option.value)
							} else {
								this.groupData.splice(this.groupData.indexOf(option.value), 1)
							}

							if (this.isGrouping) {
								this.applyGroupingLogic(option)
							}

							this.currValue.value = this.groupData.filter(item => item.indexOf('_group_value') === -1)
							this.currValue.label = this.currValue.value.map(lbl => {
								return this.selectItems.filter(res => res.value === lbl)[0].label
							}).join(', ')
						}
					})
				} else if (newVal === null || newVal === undefined) {
					this.groupData = []
					this.currValue = {
						label: null,
						value: []
					}
				}
			},
			immediate: true
		}
	},
	mounted () {
		this.$refs.mainElement.addEventListener('keydown', this.keyPress, true)
	},
	beforeDestroy () {
		this.$refs.mainElement.removeEventListener('keydown', this.keyPress, true)
	},
	methods: {
		handleMenu (action) {
			this.menuVisible = action
		},
		focusElem () {
			this.$refs.mainElement.focus()
		},
		applyGroupingLogic (option) {
			// select and unselect group option
			if (option.group) {
				const groupIndex = this.groupData.findIndex(val => option.value === val)

				this.selectItems.forEach(item => {
					const itemIndex = this.groupData.findIndex(val => item.value === val)
					// select all from group
					if (groupIndex !== -1 && itemIndex === -1 && item.groupValue === option.value) {
						this.groupData.push(item.value)
					} else if (groupIndex === -1 && itemIndex !== -1 && item.groupValue === option.value) {
						this.groupData.splice(itemIndex, 1)
					}
				})
			} else {
				const group = this.selectItems.filter(item => item.groupValue === option.groupValue)
				const groupSelected = group.every(item => this.groupData.indexOf(item.value) !== -1)
				const groupUnselected = group.every(item => this.groupData.indexOf(item.value) === -1)

				if (groupSelected && this.groupData.indexOf(option.groupValue) === -1) {
					this.groupData.push(option.groupValue)
				} else if (groupUnselected && this.groupData.indexOf(option.groupValue) !== -1) {
					this.groupData.splice(this.groupData.indexOf(option.groupValue), 1)
				}
			}
		},
		selectOption (option) {
			if (this.isGrouping) {
				this.applyGroupingLogic(option)
			}

			this.currValue.value = this.groupData.filter(item => item.indexOf('_group_value') === -1)
			this.currValue.label = this.currValue.value.map(val => {
				return this.selectItems.filter(res => res.value === val)[0].label
			}).join(', ')

			/**
       * Triggers when MultiSelect changes it's value
       * @event change
       * @property {string} value new value set
       */
			this.$emit('change', this.currValue.value.join(',').length ? this.currValue.value.join(',') : null)
		},
		pickOption (index) {
			document.getElementById(`opt${index}`).click()
			document.querySelector(`.c-MultiSelect__menu li span[key-index="${index}"]`).focus()
		},
		clearSelection () {
			if (!this.disabled) {
				this.groupData = []
				this.currValue = {
					label: null,
					value: []
				}

				this.$emit('change', null)
			}
		},
		keyPress (ev) {
			const currIndex = document.activeElement.getAttribute('key-index')
			const elemCount = document.querySelector('.c-MultiSelect__menu') ? document.querySelector('.c-MultiSelect__menu').childElementCount : 0

			if (ev.key === 'ArrowDown') {
				let index = 0

				if (currIndex !== null) {
					index = parseInt(currIndex, 10)
					index += 1
				}

				if (index <= elemCount - 1) {
					document.querySelector(`.c-MultiSelect__menu li span[key-index="${index}"]`).focus()
				}
			} else if (ev.key === 'ArrowUp') {
				let index = currIndex !== null ? parseInt(currIndex, 10) : 0
				index -= 1
				if (index >= 0) {
					document.querySelector(`.c-MultiSelect__menu li span[key-index="${index}"]`).focus()
				}
			} else if (ev.key === 'Escape') {
				document.activeElement.blur()
			} else if (ev.key === 'Backspace' && this.withClear) {
				this.clearSelection()
			}
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';
$padding-right: 60px;

.c-MultiSelect {
  $font-size-label: 16px;
  $font-size-label-top: 12px;
  $font-size-value: 16px;
  $font-size-dropdown: 14px;

  position: relative;
  outline: none;
  height: 50px;
  width: 100%;
  display: block;
  box-sizing: border-box;
  padding: calc(#{$base-padding} / 2) $padding-right
    calc(#{$base-padding} / 2) calc(#{$base-padding} / 2);
  color: $un-n700;
  border-radius: $border-radius;
  border: 1px solid $un-gray-light;
  background-color: $un-white;
  cursor: pointer;
  text-align: left;
  position: relative;

  &:disabled {
    border-color: $un-n500;
    background-color: $un-n100;
		cursor: not-allowed;

    & .c-MultiSelect__label,
    & .c-MultiSelect__value {
      color: $un-n500;
    }

    & .c-MultiSelect__clear {
			cursor: not-allowed;
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%238D98AA" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }

    & .c-MultiSelect__decorator {
      background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%238D98AA" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
    }
  }

  &::-moz-focus-inner {
    border: 0;
  }

  &.open {
    border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;
    outline: none;

    & .c-MultiSelect__label {
      color: $un-blue;
    }

    & .c-MultiSelect__clear {
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%233843d0" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }

    & .c-MultiSelect__decorator {
      background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%233843d0" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
      transform: rotate(180deg);
    }
  }

	&.isActive {
		border: 1px solid $un-blue;
    box-shadow: inset 0 0 0 1px $un-blue;
    outline: none;

    & .c-MultiSelect__label {
      color: $un-blue;
    }
	}

  &.withValue {
    .c-MultiSelect__label {
      font-size: $font-size-label-top;
      top: calc(#{$base-padding} / 2.4);
    }
  }

  &.isGrouping {
    .c-MultiSelect__menuItem {
      &.simpleOption {
        label span {
          padding-left: calc(#{$base-padding} + 20px);
        }
      }
    }
  }

  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  &__label {
    display: block;
    width: calc(100% - #{$padding-right} - calc(#{$base-padding} / 2));
    font-size: $font-size-label;
    color: $un-n700;
    position: absolute;
    top: 14px;
    left: calc(#{$base-padding} / 2);
    z-index: 2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: all $default-time $default-easing;
  }

  &__value {
    width: 100%;
    font-size: $font-size-value;
    color: $un-n700;
    letter-spacing: 0.5px;
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: calc(#{$base-margin} - 2px);
    opacity: 1;
    transition: opacity $default-time $default-easing;
  }

  &__clear {
    width: 20px;
    height: 20px;
    position: absolute;
    top: 14px;
    right: 36px;
    cursor: pointer;
    background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%231C357B" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center;
    z-index: 2;

    &:hover {
      background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="%233843d0" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/%3E%3Cpath d="M0 0h24v24H0z" fill="none"/%3E%3C/svg%3E');
    }
  }

  &__decorator {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    content: '';
    display: block;
    width: 42px;
    pointer-events: none;
    background-position: center;
    background-repeat: no-repeat;
    background-image: url('data:image/svg+xml,%3Csvg width="12" height="7" viewBox="0 0 12 7" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M5.166 4.21L8.976.4a.795.795 0 0 1 1.125 1.125l-4.409 4.41a.795.795 0 0 1-1.288-.238L.233 1.527A.795.795 0 0 1 1.358.401L5.166 4.21z" fill="%23343F50" fill-rule="evenodd"/%3E%3C/svg%3E%0A');
  }

  &__menu {
    position: absolute;
    bottom: -8px;
    left: 0;
    transform: translate(0, 100%);
    z-index: 5;
    list-style: none;
    margin: 0;
    padding: 0;
    min-width: 100%;
    max-height: calc(48px * 4);
    overflow-x: hidden;
    overflow-y: auto;
    background-color: $un-white;
    border-radius: $border-radius;
    box-shadow: 0px 3px 6px 0px rgba(174, 185, 203, 0.6), 0px 3px 6px 0px rgba(174, 185, 203, 0.6);
  }

  &__menuItem {
    margin: 0;
    padding: 0;
    height: 48px;
    display: flex;
    align-items: center;

    input[type="checkbox"] {
      width: 0;
      height: 0;
      visibility: hidden;
      opacity: 0;
      position: absolute;
    }

    &.simpleOption {
      label span {
        padding-left: $base-padding
      }

      input:checked + label span {
        &::before {
          background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3C!-- Generator: Sketch 61.2 (89653) - https://sketch.com --%3E%3Ctitle%3ECheckbox/selected%3C/title%3E%3Cdesc%3ECreated with Sketch.%3C/desc%3E%3Cg id='Checkbox/selected' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' opacity='1'%3E%3Cpath d='M14.6986428,6.27716754 C14.7038152,6.28222495 14.7089305,6.28734032 14.713988,6.29251267 L14.7209017,6.29958353 C15.1008922,6.6882107 15.1008922,7.30919077 14.7209017,7.69781794 L8.84478758,13.7074873 C8.47783666,14.0827785 7.87985994,14.0953942 7.49749137,13.7403157 C7.45564786,13.710907 7.41534638,13.6775103 7.37733117,13.6403399 L7.36198604,13.6249948 L7.36198604,13.6249948 L5.27909832,11.4947661 C4.89910778,11.106139 4.89910778,10.4851589 5.27909832,10.0965317 L5.28601205,10.0894609 C5.65888656,9.70811148 6.27030585,9.70124123 6.65165524,10.0741157 C6.6568276,10.0791732 6.66194296,10.0842885 6.66700037,10.0894609 L8.143,11.599 L13.3329996,6.29251267 C13.7058741,5.91116328 14.3172934,5.90429303 14.6986428,6.27716754 Z' id='Combined-Shape' fill='%23FFFFFF'%3E%3C/path%3E%3C/g%3E%3C/svg%3E");
        }
      }
    }

    label {
      font-size: $font-size-dropdown;
      color: $un-n900;
      text-align: left;
      width: 100%;
      height: 48px;

      span {
        cursor: pointer;
        padding: calc(#{$base-padding / 2}) calc(#{$base-padding * 2}) calc(#{$base-padding / 2}) $base-padding;
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        outline: none;

        &::-moz-focus-inner {
          border: 0;
        }

        &:hover, &:focus, &.selected:hover, &.selected:focus {
          background-color: $un-blue-light;
          color: $un-n900;
        }

        &::before {
          content: '';
          position: relative;
          width: 20px;
          height: 20px;
          border: 1px solid $un-n700;
          border-radius: $border-radius;
          background-color: #FFFFFF;
          display: block;
          margin-right: calc(#{$base-margin} / 2);
        }
      }
    }

    input:checked + label span {
      &::before {
        background-color: $un-blue;
        background-image: url("data:image/svg+xml,%3C%3Fxml version='1.0' encoding='UTF-8'%3F%3E%3Csvg width='20px' height='20px' viewBox='0 0 20 20' version='1.1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink'%3E%3C!-- Generator: Sketch 61.2 (89653) - https://sketch.com --%3E%3Ctitle%3ECheckbox/half-selected%3C/title%3E%3Cdesc%3ECreated with Sketch.%3C/desc%3E%3Cg id='Checkbox/half-selected' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd'%3E%3Crect id='Rectangle' fill='%23FFFFFF' x='5' y='9' width='10' height='2' rx='1'%3E%3C/rect%3E%3C/g%3E%3C/svg%3E");
      }
    }
  }
}
</style>
